import { useState, useEffect, useCallback, useMemo } from "react";
import { useApi } from "../../hooks/useApi";
import { useCompanyId } from "../../hooks/useCompanyId";
import { usePpAccounts } from "../../hooks/usePpAccounts";
import { EthocaConfig } from "../../types/ethocaConfig";
import { EthocaMerchant } from "../../types/ethocaDimensions";
import {
  MerchantMappingConfig,
  MerchantMappingSaveBody,
  RemapUnmappedResponse,
} from "../../types/merchantMappingConfig";
import MerchantMappingSettings from "../../components/alerts/MerchantMappingSettings";
import MisroutedDescriptorSettings from "../../components/alerts/MisroutedDescriptorSettings";
import * as Sentry from "@sentry/react";
import { Box } from "@mui/material";

export default function EthocaSettings() {
  const { get, post, put, del, loading, error: apiError } = useApi();
  const { companyId } = useCompanyId();
  const { ppAccounts } = usePpAccounts();

  const [configs, setConfigs] = useState<EthocaConfig[]>([]);
  const [ethocaMerchants, setEthocaMerchants] = useState<EthocaMerchant[]>([]);

  const fetchConfigs = useCallback(async () => {
    if (!companyId) return;
    try {
      const response = await get<EthocaConfig[]>(
        `/api/companies/${companyId}/ethoca/merchant-mappings`
      );
      setConfigs(response);
    } catch (err) {
      console.error("Failed to fetch ethoca configs:", err);
      Sentry.captureException(err);
    }
  }, [get, companyId]);

  const fetchEthocaMerchants = useCallback(async () => {
    if (!companyId) return;
    try {
      const params = `?companyId=${companyId}`;
      const response = await get<{ merchants: EthocaMerchant[] }>(
        `/api/alerts/ethoca/merchants${params}`
      );
      setEthocaMerchants(response.merchants);
    } catch (err) {
      console.error("Failed to fetch Ethoca merchants:", err);
      Sentry.captureException(err);
    }
  }, [get, companyId]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  useEffect(() => {
    fetchEthocaMerchants();
  }, [fetchEthocaMerchants]);

  const merchantNameOptions = useMemo(() => {
    const names = ethocaMerchants
      .map((m) => m.name)
      .filter((n): n is string => !!n);
    return [...new Set(names)].sort();
  }, [ethocaMerchants]);

  const sharedConfigs: MerchantMappingConfig[] = useMemo(
    () => configs.map((c) => ({ ...c, configId: c.ethocaAlertsConfigId })),
    [configs]
  );

  const handleCreate = async (body: MerchantMappingSaveBody) => {
    await post(`/api/companies/${companyId}/ethoca/merchant-mappings`, body);
  };

  const handleUpdate = async (configId: number, body: MerchantMappingSaveBody) => {
    await put(`/api/companies/${companyId}/ethoca/merchant-mappings/${configId}`, body);
  };

  const handleDelete = async (configId: number) => {
    await del(`/api/companies/${companyId}/ethoca/merchant-mappings/${configId}`);
  };

  const handleSync = async (): Promise<RemapUnmappedResponse> => {
    return await post<RemapUnmappedResponse>(`/api/alerts/ethoca/remap-unmapped`, {}, { slowApi: true });
  };

  const handleSyncCompany = async (companyId: number): Promise<RemapUnmappedResponse> => {
    return await post<RemapUnmappedResponse>(`/api/alerts/ethoca/remap-unmapped`, { company_id: companyId }, { slowApi: true });
  };

  const handleRematchCompany = async (companyId: number): Promise<RemapUnmappedResponse> => {
    return await post<RemapUnmappedResponse>(`/api/alerts/ethoca/remap-unmapped`, { company_id: companyId, mode: 'rematch' }, { slowApi: true });
  };

  return (
    <Box>
      <MerchantMappingSettings
        providerLabel="Ethoca"
        merchantFieldLabel="Ethoca Merchant Name"
        companyId={companyId}
        configs={sharedConfigs}
        ppAccounts={ppAccounts}
        merchantNameOptions={merchantNameOptions}
        loading={loading}
        apiError={apiError}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        onRefresh={fetchConfigs}
        syncFeature={{ onSync: handleSync, onSyncCompany: handleSyncCompany, onRematchCompany: handleRematchCompany }}
      />
      <Box sx={{ mt: 4 }}>
        <MisroutedDescriptorSettings companyId={companyId} />
      </Box>
    </Box>
  );
}
